/**
 * Workflow Status Role Service
 *
 * Service for managing workflow status-role permissions.
 * These permissions control which roles can view and/or transition
 * workflows in specific statuses.
 */

import { apiService } from './api';
import type { ApiResponse } from '../types';
import type {
  WorkflowStatusRole,
  SetStatusRolePermission,
  UserStatusPermissions,
} from '../types/workflow/statusRole';

/**
 * Unwraps an API response, throwing an error if unsuccessful.
 * @param response The API response to unwrap
 * @param fallbackError Error message to use if response.message is empty
 * @returns The response data
 * @throws Error if response.success is false or data is null/undefined
 */
function unwrapResponse<T>(response: ApiResponse<T>, fallbackError: string): T {
  if (!response.success || !response.data) {
    throw new Error(response.message || fallbackError);
  }
  return response.data;
}

/**
 * Service for managing workflow status-role permissions.
 */
export const workflowStatusRoleService = {
  /**
   * Get all roles and their permissions for a specific status.
   * @param statusId The workflow status ID
   * @param signal Optional AbortSignal for request cancellation
   * @returns Array of status-role permissions for the status
   */
  getRolesForStatus: async (
    statusId: number,
    signal?: AbortSignal
  ): Promise<WorkflowStatusRole[]> => {
    const response = await apiService.get<WorkflowStatusRole[]>(
      `/workflow/status-roles/status/${statusId}`,
      undefined,
      signal
    );
    return unwrapResponse(response, 'Failed to fetch roles for status');
  },

  /**
   * Get all statuses and their permissions for a specific role.
   * @param roleId The role ID
   * @param signal Optional AbortSignal for request cancellation
   * @returns Array of status-role permissions for the role
   */
  getStatusesForRole: async (
    roleId: number,
    signal?: AbortSignal
  ): Promise<WorkflowStatusRole[]> => {
    const response = await apiService.get<WorkflowStatusRole[]>(
      `/workflow/status-roles/role/${roleId}`,
      undefined,
      signal
    );
    return unwrapResponse(response, 'Failed to fetch statuses for role');
  },

  /**
   * Set or update a permission for a status-role combination.
   * Creates a new permission if none exists, otherwise updates the existing one.
   * @param permission The permission settings to apply
   * @returns The created or updated status-role permission
   */
  setPermission: async (
    permission: SetStatusRolePermission
  ): Promise<WorkflowStatusRole> => {
    const response = await apiService.post<WorkflowStatusRole>(
      '/workflow/status-roles',
      permission
    );
    return unwrapResponse(response, 'Failed to set status-role permission');
  },

  /**
   * Remove a permission for a status-role combination.
   * After removal, users with only this role will not be able to
   * view or transition workflows in this status.
   * @param statusId The workflow status ID
   * @param roleId The role ID
   */
  removePermission: async (statusId: number, roleId: number): Promise<void> => {
    const response = await apiService.delete<void>(
      `/workflow/status-roles/status/${statusId}/role/${roleId}`
    );
    // For delete operations, we just check success without expecting data
    if (!response.success) {
      throw new Error(response.message || 'Failed to remove status-role permission');
    }
  },

  /**
   * Get the current user's workflow status permissions.
   * Returns an aggregated view of all statuses the user can view
   * and transition based on all their assigned roles.
   * @param signal Optional AbortSignal for request cancellation
   * @returns The current user's aggregated status permissions
   */
  getMyPermissions: async (signal?: AbortSignal): Promise<UserStatusPermissions> => {
    const response = await apiService.get<UserStatusPermissions>(
      '/workflow/my-permissions',
      undefined,
      signal
    );
    return unwrapResponse(response, 'Failed to fetch user status permissions');
  },

  /**
   * Get all status-role permissions (admin only).
   * Returns a complete matrix of all status-role permission combinations.
   * @param signal Optional AbortSignal for request cancellation
   * @returns Array of all status-role permissions
   */
  getAllPermissions: async (signal?: AbortSignal): Promise<WorkflowStatusRole[]> => {
    const response = await apiService.get<WorkflowStatusRole[]>(
      '/workflow/status-roles',
      undefined,
      signal
    );
    return unwrapResponse(response, 'Failed to fetch all status-role permissions');
  },
};
